import { useEffect, useState } from "preact/hooks";
import { MONTHS, WEEKDAYS, fromCivil, isoDate, toCivil } from "../tz/civil";

export interface DayMarker {
  /** number of zones with a DST/offset change on this local date */
  count: number;
  /** the selected zone changes on this date */
  selected: boolean;
  zones: string[];
}

interface Props {
  year: number;
  month: number;
  day: number;
  markers: (year: number, month: number) => Map<string, DayMarker>;
  onSelect: (year: number, month: number, day: number) => void;
}

export function Calendar({ year, month, day, markers, onSelect }: Props) {
  const [view, setView] = useState({ year, month });
  useEffect(() => setView({ year, month }), [year, month]);
  const marks = markers(view.year, view.month);

  const first = toCivil(fromCivil(view.year, view.month, 1));
  const daysInMonth = toCivil(fromCivil(view.year, view.month + 1, 0)).day;
  const lead = (first.weekday + 6) % 7; // Monday-first
  const cells: (number | null)[] = [...Array(lead).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);

  const shift = (delta: number) => {
    const m = view.month - 1 + delta;
    setView({ year: view.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 + 1 });
  };
  const selectedIso = isoDate(toCivil(fromCivil(year, month, day)));

  return (
    <div class="calendar">
      <div class="calendar-head">
        <button type="button" onClick={() => shift(-12)} aria-label="Previous year" title="Previous year">«</button>
        <button type="button" onClick={() => shift(-1)} aria-label="Previous month" title="Previous month">‹</button>
        <span class="calendar-title">{MONTHS[view.month - 1]} {view.year}</span>
        <button type="button" onClick={() => shift(1)} aria-label="Next month" title="Next month">›</button>
        <button type="button" onClick={() => shift(12)} aria-label="Next year" title="Next year">»</button>
      </div>
      <div class="calendar-grid">
        {[1, 2, 3, 4, 5, 6, 0].map((d) => (
          <div key={d} class="calendar-dow">{WEEKDAYS[d]}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} class="calendar-cell empty" />;
          const iso = isoDate(toCivil(fromCivil(view.year, view.month, d)));
          const m = marks.get(iso);
          const cls = ["calendar-cell"];
          if (iso === selectedIso) cls.push("selected");
          if (m) cls.push("marked");
          if (m?.selected) cls.push("marked-selected");
          const title = m ? `${m.count} zone${m.count === 1 ? "" : "s"} change clocks: ${m.zones.slice(0, 6).join(", ")}${m.zones.length > 6 ? "…" : ""}` : "";
          return (
            <button key={iso} type="button" class={cls.join(" ")} title={title} onClick={() => onSelect(view.year, view.month, d)}>
              <span>{d}</span>
              {m && <span class="calendar-dot">{m.count > 1 ? m.count : ""}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
